import middleware from "../../../shared/middleware";
import { getCustomContextMetadata, trackConvivaCustomEvent } from "../../../shared/analytics/media";
import errorConstants from "../../../shared/constants/error";
import constants from "../../../shared/constants";
import PlayerConstants from "../../../shared/constants/player";
import { PIN_MODAL_MODES, PIN_MODAL_TYPES } from "../../../components/PinModal";
import { ACTION_VALUES, ANALYTICS_EVENT_TYPES, WEB_ACTION_EVENT_NAMES } from "../../../shared/constants/analytics";
import { getGenericErrorEventHandler } from "../../../shared/analytics/helpers";
import { getAVSPlaybackError } from "../../../shared/utils/playbackHelpers";
import { isPinMaxLimitReach } from "../../../shared/utils";
import { trackGenericAction } from "../../../shared/analytics/dataLayer";

const { ERROR_TYPES, AVS_ERROR_CODES } = errorConstants;
const { MODAL_TYPES, MVE2_AGL_VERSION } = constants;
const { ASSET_TYPES } = PlayerConstants;
const { getContentUserData } = middleware;

/**
 * Updated the details of the live stream to be used for comparison with the next stream during transition
 * @param {Object} prevLiveProgramDetails
 * @param {Object} liveProgramDetails
 * @param {Boolean} isLookbackStream
 * @param {Boolean} isRestartedStream
 */
export const updatePrevLiveProgramDetails = (
  prevLiveProgramDetails,
  liveProgramDetails,
  isLookbackStream,
  isRestartedStream
) => {
  prevLiveProgramDetails.current = {
    prevLiveProgram: { ...liveProgramDetails },
    isLookback: isLookbackStream,
    isRestarted: isRestartedStream,
  };
};

const handlePCBlocked = ({
  assetToPlay,
  pcPin,
  refetchStream,
  telusConvivaAnalytics,
  contentType,
  modalContent,
  modalType,
  translate,
  setIsCallParentalPinLock,
  showModalPopup,
  dispatch,
  isDynatraceTrackingEnabled,
}) => {
  if (pcPin.current) {
    modalContent = null;
    refetchStream(assetToPlay.assetId, pcPin.current);
  } else {
    telusConvivaAnalytics.reportParentalPinStart(assetToPlay.assetName, contentType);
    modalType = MODAL_TYPES.PIN;
    Object.assign(modalContent, {
      pinModalMode: PIN_MODAL_MODES.ACCESS,
      pinModalType: PIN_MODAL_TYPES.PARENTAL,
      pinConfirmHandler: (pin) => {
        return refetchStream(assetToPlay.assetId, pin);
      },
      pinAnalyticsErrorEventHandler: getGenericErrorEventHandler(
        ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_UNLOCK_ERROR,
        ACTION_VALUES.PARENTAL_PIN_CONTENT_UNLOCK,
        WEB_ACTION_EVENT_NAMES.PARENTAL_PIN_CONTENT_UNLOCK_ERROR,
        isDynatraceTrackingEnabled
      ),
    });

    modalContent.title = translate("enter_parental_pin");
    setIsCallParentalPinLock(true);
    dispatch(showModalPopup(modalType, modalContent));
  }
};

const handleOtherEntitlementErrors = ({
  entitlement,
  modalContent,
  translate,
  showModalPopup,
  dispatch,
  modalType,
}) => {
  if (entitlement.isContentOOHBlocked) {
    modalContent.message = translate("message_in_home");
  } else if (entitlement.isGeofencedBlocked) {
    modalContent.message = translate("error_outside_region");
  } else if (entitlement.isSportBlackoutBlocked) {
    modalContent.message = translate("error_program_restricted");
  } else if (entitlement.isPlatformBlacklisted) {
    modalContent.message = translate("error_program_not_available_device");
  } else if (entitlement.isChannelNotSubscribed) {
    modalContent.message = translate("message_subscribe");
  } else if (entitlement.isGeoBlocked) {
    modalContent.message = translate("error_program_not_available_region");
  }
  dispatch(showModalPopup(modalType, modalContent));
};

export const handleEntitlementError = async ({
  err,
  appProvider,
  currentChannelId,
  contentType,
  cancelTokenSource,
  restartAssetId,
  pcPin,
  refetchStream,
  telusConvivaAnalytics,
  translate,
  history,
  showModalPopup,
  dispatch,
  setIsCallParentalPinLock,
  getMediaNodeMetaData,
  isDynatraceTrackingEnabled,
}) => {
  if (err?.code?.includes(AVS_ERROR_CODES.USER_NO_RIGHTS) || AVS_ERROR_CODES.INCORRECT_PIN_CODES.includes(err?.code)) {
    try {
      const res = await getContentUserData(
        appProvider,
        currentChannelId,
        contentType,
        cancelTokenSource.current,
        restartAssetId.current
      );

      if (res && res.containers && res.containers.length > 0) {
        const entitlement = res.containers[0].entitlement;
        const assets = entitlement?.assets;
        const assetToPlay = assets.find((asset) => asset.assetType.toLowerCase() === ASSET_TYPES.MASTER);
        let modalContent = {
          title: ERROR_TYPES.INFO,
          history,
          isCloseable: true,
          analyticsInfo: {
            errorCode: res.errorDescription,
            media: getMediaNodeMetaData(),
          },
        };
        let modalType = MODAL_TYPES.ERROR;

        if (entitlement.isPCBlocked) {
          handlePCBlocked({
            assetToPlay,
            pcPin,
            refetchStream,
            telusConvivaAnalytics,
            contentType,
            modalContent,
            modalType,
            translate,
            setIsCallParentalPinLock,
            showModalPopup,
            dispatch,
            isDynatraceTrackingEnabled,
          });
        } else {
          handleOtherEntitlementErrors({
            entitlement,
            modalContent,
            translate,
            showModalPopup,
            dispatch,
            modalType,
          });
        }
      }
    } catch (err) {
      console.error(err);
    }
  }
};

export const handleFetchStreamResponse = ({
  res,
  tokenOnly,
  parentalPin,
  pcPin,
  pcPinTimer,
  restartPcPinTimer,
  getMediaNodeMetaData,
  isConvivaAppTrackerEnabled,
  userProfile,
  appProvider,
  isInHome,
  isCTTEnabled,
  restartLiveStream,
  isLookbackStream,
  setVideoUrl,
  setAuthToken,
  removeLocalStorage,
  setLocalStorage,
  VIDEO_AUTH_TOKEN,
  setLivePlaybackCanStart,
  showPlaybackError,
  dispatch,
  isDynatraceTrackingEnabled,
}) => {
  if (!tokenOnly) {
    if (pcPin.current === null) {
      pcPin.current = parentalPin;
      if (!pcPinTimer.current) {
        restartPcPinTimer();
      }

      if (parentalPin) {
        trackGenericAction(
          ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_UNLOCK,
          getMediaNodeMetaData(),
          isDynatraceTrackingEnabled
        );
        if (isConvivaAppTrackerEnabled) {
          trackConvivaCustomEvent(
            ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_UNLOCK,
            getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
          );
        }
      }
    }
    if (!isCTTEnabled || restartLiveStream || isLookbackStream) {
      setVideoUrl(res.src);
      if (res.token) {
        setAuthToken(res.token);
        removeLocalStorage(VIDEO_AUTH_TOKEN);
        setLocalStorage(VIDEO_AUTH_TOKEN, res.token, 90000);
      }
    }
    setLivePlaybackCanStart(true);
    dispatch(showPlaybackError(null, null));
  }
};

export const handleFetchStreamError = ({
  err,
  parentalPin,
  appProvider,
  telusConvivaAnalytics,
  translate,
  history,
  showModalPopup,
  dispatch,
  setFatalPlaybackError,
  showPlaybackError,
}) => {
  if (
    parentalPin &&
    (appProvider.AGL_Version === MVE2_AGL_VERSION
      ? err?.code?.includes(AVS_ERROR_CODES.USER_NO_RIGHTS)
      : AVS_ERROR_CODES.INCORRECT_PIN_CODES.includes(err?.code))
  ) {
    telusConvivaAnalytics.reportParentalPinFailed(false, err);
    throw err;
  } else if (parentalPin && isPinMaxLimitReach(err)) {
    telusConvivaAnalytics.reportParentalPinFailed(true, err);
    throw err;
  } else if (err?.code === AVS_ERROR_CODES.PANIC_MODE || err?.code === AVS_ERROR_CODES.PC_LEVEL_BLOCKED_DURING_ECE) {
    telusConvivaAnalytics.reportPlaybackError(err);

    const modalContent = {
      title: translate("programming_unavailable"),
      history,
      isCloseable: true,
      message: translate("programming_unavailable_body")?.replace("%s", appProvider.pcLevelLabel),
    };

    dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
  } else {
    telusConvivaAnalytics.reportPlaybackError(err);

    console.error(err);
    let AVSError = getAVSPlaybackError(err);
    setFatalPlaybackError(AVSError);
    dispatch(showPlaybackError(AVSError.code, AVSError.message));
  }
};
